import { useTranslation } from '@exam-room/translations';
import { cn } from '@exam-room/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, Form, LoadingSpinner } from '../../../../../components';
import { ExamCenterSatisfactionRow, RadioGroup } from '../../components';

import {
  type QuestionnaireShortFormInput,
  type QuestionnaireShortFormOutput,
  QuestionnaireShortFormSchema,
} from './questionnaire-short-form-schema';

const formLabelClassName = 'flex gap-2 text-base font-semibold';
const labelSpanText = 'after:text-danger after:ml-1 after:content-["*"]';

interface QuestionnaireShortFormProps extends Omit<React.ComponentProps<'form'>, 'onSubmit'> {
  onSubmit: (data: QuestionnaireShortFormOutput) => Promise<void>;
}

export default function QuestionnaireShortForm(props: Readonly<QuestionnaireShortFormProps>) {
  const { className, onSubmit, ...restProps } = props;
  const { t } = useTranslation();

  const formContext = useForm<QuestionnaireShortFormInput, undefined, QuestionnaireShortFormOutput>(
    {
      defaultValues: {
        age: '',
        highestEducationLevel: { value: '', other: '' },
        currentOrganization: { value: '', other: '' },
        examAttendingObjectives: { value: '', other: '' },
        examinationSatisfaction: {
          location: '',
          staffServices: '',
          systemUsability: '',
          systemStability: '',
        },
        comment: '',
      },
      resolver: zodResolver(QuestionnaireShortFormSchema),
    },
  );

  // Clear the free-text "other" input whenever the paired radio moves off "other".
  useEffect(() => {
    const subscription = formContext.watch((value, { name, type }) => {
      if (type !== 'change') return;
      const fields = [
        'highestEducationLevel',
        'currentOrganization',
        'examAttendingObjectives',
      ] as const;
      for (const field of fields) {
        if (name === `${field}.value` && value[field]?.value !== 'other') {
          if (value[field]?.other) formContext.setValue(`${field}.other`, '');
          formContext.clearErrors(`${field}.other`);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [formContext]);

  const highestEducationLevelOptions = [
    {
      label: t('questionnaire.highestEducationLevel.options.belowBachelorDegree'),
      value: 'below-bachelor-degree',
    },
    {
      label: t('questionnaire.highestEducationLevel.options.currentlyInBachelorDegree'),
      value: 'currently-in-bachelor-degree',
    },
    {
      label: t('questionnaire.highestEducationLevel.options.bachelorDegree'),
      value: 'bachelor-degree',
    },
    {
      label: t('questionnaire.highestEducationLevel.options.masterDegreeOrHigher'),
      value: 'master-degree-or-higher',
    },
    {
      label: t('questionnaire.option.other'),
      value: 'other',
      endAdornment: (
        <div className='w-95'>
          <Form.Input
            name='highestEducationLevel.other'
            className='h-10 placeholder:font-light'
            disabled={!(formContext.watch('highestEducationLevel.value') === 'other')}
            placeholder={t('questionnaire.highestEducationLevel.inputPlaceholder')}
          />
        </div>
      ),
    },
  ];

  const shouldDisableCurrentOrganizationOther = !(
    formContext.watch('currentOrganization.value') === 'other'
  );

  const currentOrganizationOptions = [
    { label: t('questionnaire.currentOrganization.option.studying'), value: 'studying' },
    {
      label: t('questionnaire.currentOrganization.option.securities-company'),
      value: 'securities-company',
    },
    {
      label: t('questionnaire.currentOrganization.option.asset-management-company'),
      value: 'asset-management-company',
    },
    { label: t('questionnaire.currentOrganization.option.bank'), value: 'bank' },
    {
      label: t('questionnaire.currentOrganization.option.insurance-company'),
      value: 'insurance-company',
    },
    {
      label: t('questionnaire.currentOrganization.option.government'),
      value: 'government',
    },
    {
      label: t('questionnaire.currentOrganization.option.private-organization'),
      value: 'private-organization',
    },
    {
      label: t('questionnaire.currentOrganization.option.freelance'),
      value: 'freelance',
    },
    {
      label: t('questionnaire.option.other'),
      value: 'other',
      endAdornment: (
        <div className='w-95'>
          <Form.Input
            name='currentOrganization.other'
            className='h-10 placeholder:font-light'
            disabled={shouldDisableCurrentOrganizationOther}
            placeholder={t('questionnaire.currentOrganization.inputPlaceholder')}
          />
        </div>
      ),
    },
  ];

  const shouldDisableObjectiveOther = !(
    formContext.watch('examAttendingObjectives.value') === 'other'
  );

  const examAttendingObjectivesOptions = [
    {
      label: t('questionnaire.examAttendingObjectives.option.career-opportunity'),
      value: 'career-opportunity',
    },
    {
      label: t('questionnaire.examAttendingObjectives.option.register-license-and-profession'),
      value: 'register-license-and-profession',
    },
    {
      label: t('questionnaire.examAttendingObjectives.option.self-assessment'),
      value: 'self-assessment',
    },
    {
      label: t(
        'questionnaire.examAttendingObjectives.option.special-project-with-outside-institution',
      ),
      value: 'special-project-with-outside-institution',
    },
    {
      label: t('questionnaire.option.other'),
      value: 'other',
      endAdornment: (
        <div className='w-95'>
          <Form.Input
            name='examAttendingObjectives.other'
            className='h-10 placeholder:font-light'
            disabled={shouldDisableObjectiveOther}
            placeholder={t('questionnaire.examAttendingObjectives.inputPlaceholder')}
          />
        </div>
      ),
    },
  ];

  const satisfactionColumns = [
    { label: t('questionnaire.examinationSatisfaction.option.very-good'), value: 'very-good' },
    { label: t('questionnaire.examinationSatisfaction.option.good'), value: 'good' },
    { label: t('questionnaire.examinationSatisfaction.option.fair'), value: 'fair' },
    {
      label: t('questionnaire.examinationSatisfaction.option.need-improvement'),
      value: 'need-improvement',
    },
  ];

  const satisfactionRows = [
    {
      field: 'location',
      label: t('questionnaire.examinationSatisfaction.criteria.location'),
    },
    {
      field: 'staffServices',
      label: t('questionnaire.examinationSatisfaction.criteria.staff-services'),
    },
  ] as const;

  const satisfactionSystemRows = [
    {
      field: 'systemUsability',
      label: t('questionnaire.examinationSatisfaction.criteria.system-usability'),
    },
    {
      field: 'systemStability',
      label: t('questionnaire.examinationSatisfaction.criteria.system-stability'),
    },
  ] as const;

  const satisfactionGridClassName =
    'grid w-full grid-cols-[1fr_repeat(4,minmax(80px,140px))] items-center';
  const satisfactionDividerClassName = 'w-full border-neutral-300 border-t';

  const handleSubmit = async (data: QuestionnaireShortFormOutput) => {
    await onSubmit(data);
  };

  return (
    <FormProvider {...formContext}>
      <form
        className={cn('flex flex-col gap-6', className)}
        onSubmit={formContext.handleSubmit(handleSubmit)}
        {...restProps}
      >
        <div className='grid max-w-95 gap-2 placeholder:font-light'>
          <label htmlFor='age' className={formLabelClassName}>
            <span>1.</span>
            <span className={labelSpanText}>{t('questionnaire.age.questionTitle')}</span>
          </label>
          <div className='ml-5.5'>
            <Form.Input
              name='age'
              placeholder={t('questionnaire.age.inputPlaceholder')}
              type='number'
              className='h-10'
            />
          </div>
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='highestEducationLevel' className={formLabelClassName}>
            <span>2.</span>
            <span className={labelSpanText}>
              {t('questionnaire.highestEducationLevel.questionTitle')}
            </span>
          </label>
          <RadioGroup
            name='highestEducationLevel.value'
            className='ml-3.5'
            options={highestEducationLevelOptions}
          />
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='currentOrganization' className={formLabelClassName}>
            <span>3.</span>
            <span className={labelSpanText}>{t('questionnaire.currentOrganization.title')}</span>
          </label>
          <RadioGroup
            name='currentOrganization.value'
            className='ml-3.5'
            options={currentOrganizationOptions}
          />
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='examAttendingObjectives' className={formLabelClassName}>
            <span>4.</span>
            <span className={labelSpanText}>
              {t('questionnaire.examAttendingObjectives.title')}
            </span>
          </label>
          <RadioGroup
            name='examAttendingObjectives.value'
            className='ml-3.5'
            options={examAttendingObjectivesOptions}
          />
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='examinationSatisfaction' className={formLabelClassName}>
            <span>5.</span>
            <span className={labelSpanText}>
              {t('questionnaire.examinationSatisfaction.title')}
            </span>
          </label>
          <div className='ml-5.5 flex flex-col'>
            <div className={cn(satisfactionGridClassName, 'pb-3')}>
              <div />
              {satisfactionColumns.map((column) => (
                <p key={column.value} className='text-center font-semibold'>
                  {column.label}
                </p>
              ))}
            </div>
            <div className={satisfactionDividerClassName} />
            <ExamCenterSatisfactionRow
              field={satisfactionRows[0].field}
              label={satisfactionRows[0].label}
              columns={satisfactionColumns}
              gridClassName={satisfactionGridClassName}
            />
            <div className={satisfactionDividerClassName} />
            <ExamCenterSatisfactionRow
              field={satisfactionRows[1].field}
              label={satisfactionRows[1].label}
              columns={satisfactionColumns}
              gridClassName={satisfactionGridClassName}
            />
            <div className={satisfactionDividerClassName} />
            <p className='py-2 font-semibold'>
              {t('questionnaire.examinationSatisfaction.systemGroup')}
            </p>
            {satisfactionSystemRows.map((row) => (
              <ExamCenterSatisfactionRow
                key={row.field}
                field={row.field}
                label={row.label}
                columns={satisfactionColumns}
                gridClassName={satisfactionGridClassName}
              />
            ))}
            <div className={satisfactionDividerClassName} />
            {formContext.formState.errors.examinationSatisfaction ? (
              <p className='text-danger mt-2 text-sm'>
                {t('questionnaire.examinationSatisfaction.requiredError')}
              </p>
            ) : null}
          </div>
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='comment' className={formLabelClassName}>
            <span>6.</span>
            <span>{t('questionnaire.comment.title')}</span>
          </label>
          <div className='w-full'>
            <Form.Textarea
              name='comment'
              placeholder={t('questionnaire.comment.inputPlaceholder')}
              className='w-full'
            />
          </div>
        </div>

        <Button
          type='submit'
          className='mx-auto flex gap-2'
          disabled={formContext.formState.isSubmitting}
        >
          {formContext.formState.isSubmitting ? <LoadingSpinner /> : null}
          {t('questionnaire.submit')}
        </Button>
      </form>
    </FormProvider>
  );
}
